import { useState, useEffect, useCallback } from "react";
import moment from "moment";
import toast from "react-hot-toast";
import { agentInvitationService } from "../../services/api";
import PartnerConfirmDialog from "./PartnerConfirmDialog";

// Pending agent invitations (design 1a). Collapsible list with resend +
// revoke (revoke is destructive). Admin-only; self-contained data fetch.
// `reloadKey` bumps after a new invite is sent so the list refreshes.

// T057 — the row's role label renders STORED data only.
//
// The old fallback returned "Agent" for a blank/null role_type, so a row
// whose invite never recorded a role (legacy rows predate the field) still
// claimed "Invited as Agent". No role stored → the row says nothing about
// a role at all; it never invents one.
//
// The comparison is also normalised: the backend persists the enum values
// "Agent" / "AgencyAdmin" (AgentInviteSerializer.ROLE_CHOICES), which the
// old lowercase-only checks missed — "AgencyAdmin" fell through and
// rendered as the raw enum token. Any other stored value is shown
// verbatim rather than mapped to something it is not.
const roleLabel = (r) => {
  const key = String(r || "").trim().toLowerCase().replace(/[\s_-]/g, "");
  if (!key) return null;
  if (key === "agencyadmin") return "Agency admin";
  if (key === "agent") return "Agent";
  return String(r).trim();
};

// The row's display identity, also the name the confirm dialog says out loud.
// Email first because that is the only channel an agent invite is delivered on.
const inviteeLabel = (inv) => {
  const name = [inv?.first_name, inv?.last_name].filter(Boolean).join(" ");
  return inv?.email || inv?.phone || name || "Invitee";
};

const PendingInvitationsPanel = ({ reloadKey = 0 }) => {
  const [invites, setInvites] = useState([]);
  // collapsed by default per board 1l (Marin 2026-08-04) — the row shows the
  // amber count; tapping the header opens the list.
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);
  // DECISION-037 — Revoke is destructive with no undo (the invite is gone and
  // the row disappears optimistically), so it asks first. Holds the invite
  // awaiting confirmation; null = no dialog.
  const [revokeTarget, setRevokeTarget] = useState(null);

  const load = useCallback(async () => {
    const res = await agentInvitationService.getPendingAgentInvitations();
    if (res.success) setInvites(Array.isArray(res.data) ? res.data : []);
  }, []);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  const handleResend = async (inv) => {
    setBusyId(inv.id);
    try {
      const res = await agentInvitationService.resendAgentInvitation(inv.id);
      if (res.success) {
        toast.success("Invitation resent.");
        load();
      } else {
        toast.error(res.error || "Failed to resend");
      }
    } finally {
      setBusyId(null);
    }
  };

  const handleRevoke = async (inv) => {
    setBusyId(inv.id);
    try {
      const res = await agentInvitationService.revokeAgentInvitation(inv.id);
      if (res.success) {
        toast.success("Invitation revoked.");
        setInvites((list) => list.filter((i) => i.id !== inv.id));
      } else {
        toast.error(res.error || "Failed to revoke");
      }
    } finally {
      setBusyId(null);
    }
  };

  // The dialog's confirm: run the UNCHANGED handler, then close. handleRevoke
  // swallows its own errors, so the dialog always closes exactly once.
  const confirmRevoke = async () => {
    if (!revokeTarget) return;
    await handleRevoke(revokeTarget);
    setRevokeTarget(null);
  };

  if (!invites.length) return null;

  return (
    <div className="mx-6 mt-4 bg-white border border-[#E3E9F1] rounded-xl shadow-sm">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-2.5 px-4 py-3 text-left">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg>
        <span className="text-[13.5px] font-semibold text-[#0F172A]">Pending invitations</span>
        <span className="text-[11.5px] font-semibold px-1.5 py-0.5 rounded-full bg-[#FEF3C7] text-[#92400E]">{invites.length}</span>
        <svg className={`ml-auto transition-transform ${open ? "" : "-rotate-90"}`} width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m18 15-6-6-6 6" /></svg>
      </button>

      {open &&
        invites.map((inv) => {
          const contact = inviteeLabel(inv);
          const sent = inv.last_sent_at || inv.created_at;
          // T057: each meta segment appears only when its data exists, so
          // a row with no stored role simply drops that segment instead of
          // showing an invented one.
          const role = roleLabel(inv.role_type);
          const meta = [
            role ? `Invited as ${role}` : null,
            sent ? `sent ${moment(sent).fromNow()}` : null,
            inv.resend_count ? `resent ${inv.resend_count}×` : null,
          ]
            .filter(Boolean)
            .join(" · ");
          return (
            <div key={inv.id} className="flex items-center gap-3 px-4 py-2.5 border-t border-[#F0F3F8]">
              <div className="w-8 h-8 rounded-full border-[1.5px] border-dashed border-[#C4CEDA] flex items-center justify-center flex-none">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m2 7 10 6 10-6" /></svg>
              </div>
              <div className="flex-1 min-w-0 flex flex-col">
                <span className="text-[13.5px] font-medium text-[#0F172A] whitespace-nowrap overflow-hidden text-ellipsis">{contact}</span>
                <span className="text-[12px] text-[#94A3B8]">{meta}</span>
              </div>
              <button
                onClick={() => handleResend(inv)}
                disabled={busyId === inv.id}
                className="text-[13px] font-semibold text-[#1668D9] px-2 py-1 rounded-md hover:bg-[#EAF3FF] disabled:opacity-50"
              >
                Resend
              </button>
              <button
                onClick={() => setRevokeTarget(inv)}
                disabled={busyId === inv.id}
                className="text-[13px] font-semibold text-[#DC2626] px-2 py-1 rounded-md hover:bg-[#FEF2F2] disabled:opacity-50"
              >
                Revoke
              </button>
            </div>
          );
        })}

      {/* DECISION-037 — the existing 4c confirm, kind "revoke": its copy
          ("Revoke invite to {name}?" / "They’ll no longer be able to accept
          it. You can send a new invite anytime.") describes this action exactly,
          so no second dialog and no new wording. Close is blocked while the
          request is in flight, same rule as the partner confirms. */}
      <PartnerConfirmDialog
        action={
          revokeTarget
            ? { kind: "revoke", partner: { name: inviteeLabel(revokeTarget) } }
            : null
        }
        loading={busyId === revokeTarget?.id}
        onCancel={() =>
          busyId === revokeTarget?.id ? null : setRevokeTarget(null)
        }
        onConfirm={confirmRevoke}
      />
    </div>
  );
};

export default PendingInvitationsPanel;
